import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _object_spread_props from "@swc/helpers/src/_object_spread_props.mjs";
import React from 'react';
import readingTime from 'reading-time';
import { getFeature } from '../../DaEditorWrapper/Features/helper';
import { FIRST_IMAGE_FEATURE, WORD_COUNT_FEATURE, MEDIA_COUNT_FEATURE } from '../../DaEditorWrapper/Features';
import get from 'lodash/get';
import MediaService, { fallbackToLargestFile } from '../../utils/DeviationMediaService';
import ddt from '@wix/da-ddt';
import { getDeviationType } from '../_helpers';
import { DeviationTypes } from '../../types/deviation';
function coverDeviationExists(source) {
    return !!source.deviation;
}
export const getCoverImageAttributes = (source)=>{
    const preview = getCoverImageFile(source);
    if (!preview) {
        return;
    }
    const style = {
        backgroundImage: `url('${preview.src}')`,
        backgroundRepeat: 'no-repeat',
        backgroundPositionX: 'center',
        backgroundPositionY: 'center',
        backgroundSize: 'cover'
    };
    return {
        style
    };
};
export function getCoverImageFile(source) {
    if (!coverDeviationExists(source)) {
        return;
    }
    if (source.deviation && source.deviation.coverImage) {
        return getCoverImageWithDraftJSType(source);
    }
    return get(source.deviation, 'textContent.html.type') === 'writer' ? getCoverImageWithWriterType(source) : getCoverImageWithDraftJSType(source);
}
function getDraftJSFirstImage(source) {
    // See if we have first image feature, newer journals wont have anything
    const firstImageFeature = getFeature(FIRST_IMAGE_FEATURE, get(source.deviation, 'textContent.html.features', []));
    const firstImage = get(firstImageFeature, 'data.deviation');
    firstImage && ddt.log('journalcover', 'Journal using first image', source.deviation);
    return firstImage;
}
function getDraftJSCoverImage({ deviation  }) {
    // cover image can either be inside the coverImage property or
    // if it's not a text deviation it's the passed deviation itself
    const { coverImage: sourceCoverImage , textContent: sourceTextContent  } = deviation;
    if (sourceCoverImage) {
        return sourceCoverImage;
    }
    if (!sourceTextContent) {
        return deviation;
    }
}
/**
 * DraftJS has two versions:
 * - Legacy first image was used in beta stages
 * - CoverImage was when we let users select the cover
 */ function getCoverImageWithDraftJSType(source) {
    var _getDraftJSCoverImage;
    // Prefer coverImage over firstImage
    const deviation = (_getDraftJSCoverImage = getDraftJSCoverImage(source)) !== null && _getDraftJSCoverImage !== void 0 ? _getDraftJSCoverImage : getDraftJSFirstImage(source);
    if (!deviation) {
        return;
    }
    // Finally we try and find a image that will cover the requested block size
    return getCoverImageForSource(_object_spread_props(_object_spread({}, source), {
        deviation
    }));
}
function getCoverImageWithWriterType(source) {
    if (getDeviationType(source.deviation) === DeviationTypes.STATUS) {
        var _source_deviation_status_items_find, _source_deviation_status, _source_deviation_status_items;
        const deviation = (_source_deviation_status_items_find = (_source_deviation_status = source.deviation.status) === null || _source_deviation_status === void 0 ? void 0 : (_source_deviation_status_items = _source_deviation_status.items) === null || _source_deviation_status_items === void 0 ? void 0 : _source_deviation_status_items.find((item)=>item.type === 'thumb_background_deviation')) === null || _source_deviation_status_items_find === void 0 ? void 0 : _source_deviation_status_items_find.deviation;
        return deviation ? getCoverImageForSource(_object_spread_props(_object_spread({}, source), {
            deviation
        })) : undefined;
    }
    return getCoverImageForSource(source);
}
function getCoverImageForSource({ deviation , width =300 , height =300 , allowHuge  }) {
    if (deviation.isBlocked) {
        return;
    }
    return MediaService.getSmallestThumbThatFullyCovers(deviation, width, height, fallbackToLargestFile, {
        allowPreview: allowHuge,
        allowFullview: allowHuge
    });
}
export const getReadingTimeMinutes = (textContent)=>{
    if (!textContent) {
        return 0;
    }
    if (!isTextContentDraftJs(textContent)) {
        if (!textContent.html.markup) {
            return 0;
        }
        // Wrong but meh...
        return Math.ceil(readingTime(textContent.html.markup).minutes);
    }
    const counts = getFeature(WORD_COUNT_FEATURE, textContent.html.features);
    if (!counts) {
        return 0;
    }
    return Math.ceil(counts.data.words / 200);
};
export const isTextContentDraftJs = (textContent)=>!!(textContent && textContent.html && textContent.html.type === 'draft');
export const textContentHasMedia = (textContent)=>{
    if (!isTextContentDraftJs(textContent)) {
        return false;
    }
    const mediaCountFeature = getFeature(MEDIA_COUNT_FEATURE, textContent.html.features);
    const mediaCounts = get(mediaCountFeature, 'data', {});
    return mediaCounts && (mediaCounts['images'] > 0 || mediaCounts['video'] > 0);
};
export function getTranslationKeyForAccessibleDeviationLink(t, deviation) {
    var _deviation_author;
    const type = getDeviationType(deviation);
    const args = {
        title: deviation.title,
        author: (_deviation_author = deviation.author) === null || _deviation_author === void 0 ? void 0 : _deviation_author.username
    };
    switch(type){
        case DeviationTypes.IMAGE:
            return t('deviationview.a11y.link.visualArt', args);
        case DeviationTypes.JOURNAL:
            return t('deviationview.a11y.link.journal', args);
        case DeviationTypes.LITERATURE:
            return t('deviationview.a11y.link.literature', args);
        case DeviationTypes.FILM:
            return t('deviationview.a11y.link.film', args);
        case DeviationTypes.PDF:
            return t('deviationview.a11y.link.pdf', args);
        case DeviationTypes.POLL:
            return t('deviationview.a11y.link.poll', args);
        case DeviationTypes.COMMISSION:
            return t('deviationview.a11y.link.commission', args);
        case DeviationTypes.STATUS:
            return t('deviationview.a11y.link.status', args);
        case DeviationTypes.TIER:
            return t('deviationview.a11y.link.tier', args);
        case DeviationTypes.UNKNOWN:
        default:
            return t('deviationview.a11y.link.unknown', args);
    }
}
